import { Skeleton, Switch, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { Fragment, useCallback, useContext } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { debounce } from "shared/helpers";
import { requiredRules } from "shared/helpers/validators";
import BasicSelect from "shared/ui/Select";
import TextInput from "shared/ui/TextInput";
import { selectSurveyInfo } from "store/slicers/campaignDetail";
import { selectCampaignLoading } from "store/slicers/common";
import { CampaignDetailsContext } from "../..";
import { ECampaignSurveyType } from "../LeftSidebar/constants";
import { CommentCharacterLimit, MultipleChoiceLimitOptions } from "./constants";
import { ERequestStatus } from "store/enums/index.enum";

const startRatingDynamicTypes = [
  ECampaignSurveyType.Rating,
  ECampaignSurveyType.CustomStar,
  ECampaignSurveyType.CustomerEffortScore,
  ECampaignSurveyType.CustomerSatisfactionScore,
];

const QuestionTab = () => {
  const { watch, control, trigger, getValues, reset } = useFormContext();
  const surveyInfo = useSelector(selectSurveyInfo);
  const campaignLoadingState = useSelector(selectCampaignLoading);

  const { onSubmit, setAlreadySubmited, refetchSurveyData } = useContext(CampaignDetailsContext);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSubmit = useCallback(
    debounce(async () => {
      setAlreadySubmited(true);
      reset(getValues());
      const meta = await onSubmit(getValues());
      if (meta?.requestStatus !== ERequestStatus.FULFILLED) {
        return;
      }
      await refetchSurveyData();
    }, 2000),
    []
  );

  const onSubmitHandler = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }
    debouncedSubmit();
  };

  const multipleRule = {
    max: {
      message: `The value must be less than or equal to 
  ${watch("answers").length}`,
      value: watch("answers").length,
    },
    min: {
      message: "The value must be more than 1",
      value: 1,
    },
    validate: () => {
      return (
        Number(watch("multipleConfig.multipleMin")) <
        Number(watch("multipleConfig.multipleMax")) ||
        "Min limit must be less than Max limit"
      );
    },
  };

  const multipleRuleExact = {
    max: {
      message: `The value must be less than or equal to 
  ${watch("answers").length}`,
      value: watch("answers").length,
    },
    min: {
      message: "The value must be more than 1",
      value: 1,
    },
  };

  const commentRules = {
    validate: () => {
      return (
        Number(watch("commentConfig.commentMin")) <
        Number(watch("commentConfig.commentMax")) ||
        "Comment min limit must be less than max limit"
      );
    },
  };

  const metricRules = {
    max: {
      value: 10,
      message: "Value must be less than or equal to 10",
    },
    validate: () => {
      if (
        Number(watch("metricConfig.customEndLength")) <
        Number(watch("metricConfig.customStartLength"))
      ) {
        return "Range start must be less than range end";
      }

      if (
        Number(watch("metricConfig.customEndLength")) -
        Number(watch("metricConfig.customStartLength")) <
        2
      ) {
        return "The range must include at least 3 values";
      }
      return true;
    },
  };

  const handleBlurTrigger = () => {
    trigger();
  };

  return (
    <Box p={2}>
      {campaignLoadingState ? (
        <>
          <Skeleton
            variant="rounded"
            animation="wave"
            width={"100%"}
            height="53px"
            sx={{ mb: 2 }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            width={"100%"}
            height="53px"
            sx={{ mb: 2 }}
          />
          <Skeleton
            variant="rounded"
            animation="wave"
            width={"100%"}
            height="53px"
            sx={{ mb: 2 }}
          />
        </>
      ) : (
        <>
          {surveyInfo.details?.type !== Number(ECampaignSurveyType.Final) && (
            <Fragment>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TextInput
                  name="buttonText"
                  label="Button Text"
                  onChangeCb={onSubmitHandler}
                  rules={{
                    ...requiredRules,
                    maxLength: {
                      value: 14,
                      message: "Max length is 14 symbols",
                    },
                    minLength: {
                      value: 2,
                      message: "Min length is 2 symbols",
                    },
                  }}
                />
                {!watch("isRequired") &&
                  surveyInfo.details?.type !==
                  Number(ECampaignSurveyType.Welcome) && (
                    <TextInput
                      name="skipText"
                      label="Skip Text"
                      onChangeCb={onSubmitHandler}
                      rules={{
                        ...requiredRules,
                        maxLength: {
                          value: 14,
                          message: "Max length is 14 symbols",
                        },
                        minLength: {
                          value: 2,
                          message: "Min length is 2 symbols",
                        },
                      }}
                    />
                  )}
                {surveyInfo.details?.type ===
                  Number(ECampaignSurveyType.Comment) && (
                    <TextInput
                      name="commentText"
                      label="Comment Text"
                      onChangeCb={onSubmitHandler}
                      rules={{
                        ...requiredRules,
                        maxLength: {
                          value: 30,
                          message: "Max length is 14 symbols",
                        },
                        minLength: {
                          value: 2,
                          message: "Min length is 2 symbols",
                        },
                      }}
                    />
                  )}
              </Box>
              {surveyInfo.details?.type !== Number(ECampaignSurveyType.Final) &&
                surveyInfo.details?.type !==
                Number(ECampaignSurveyType.Welcome) && (
                  <Box display="flex" my={2} ml={0.5} alignItems={"center"}>
                    <Typography>Mandatory</Typography>
                    <Controller
                      name="isRequired"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          color="success"
                          {...field}
                          checked={field.value || false}
                          onChange={async () => {
                            const newValue = !field.value;
                            field.onChange(newValue);
                            
                            // Only make request when switching ON
                            if (newValue) {
                              setAlreadySubmited(true);
                              reset(getValues());
                              const meta = await onSubmit(getValues());
                              if (meta?.requestStatus !== ERequestStatus.FULFILLED) {
                                return;
                              }
                              await refetchSurveyData();
                            }
                          }}
                        />
                      )}
                    />
                  </Box>
                )}

              {surveyInfo.details?.type ===
                Number(ECampaignSurveyType.MultipleChoice) && (
                  <Fragment>
                    <BasicSelect
                      size="small"
                      name="multipleConfig.multipleType"
                      label={"Choice Limit"}
                      labelProp="name"
                      valueProp="value"
                      defaultValue={1}
                      options={MultipleChoiceLimitOptions}
                    />
                    {watch("multipleConfig.multipleType") === 2 && (
                      <Box mt={2}>
                        <TextInput
                          type="number"
                          name="multipleConfig.multipleExact"
                          label="Limit"
                          rules={{
                            ...requiredRules,
                            ...multipleRuleExact,
                          }}
                        />
                      </Box>
                    )}
                    {watch("multipleConfig.multipleType") === 3 && (
                      <Fragment>
                        <Box mt={2}>
                          <TextInput
                            type="number"
                            name="multipleConfig.multipleMin"
                            onChangeCb={onSubmitHandler}
                            label="Min limit"
                            onBlur={handleBlurTrigger}
                            rules={{ ...requiredRules, ...multipleRule }}
                          />
                        </Box>
                        <Box mt={2}>
                          <TextInput
                            type="number"
                            name="multipleConfig.multipleMax"
                            onChangeCb={onSubmitHandler}
                            label="Max limit"
                            onBlur={handleBlurTrigger}
                            rules={{ ...requiredRules, ...multipleRule }}
                          />
                        </Box>
                      </Fragment>
                    )}
                  </Fragment>
                )}
              {surveyInfo.details?.type ===
                Number(ECampaignSurveyType.Comment) && (
                  <Fragment>
                    <BasicSelect
                      size="small"
                      name="commentConfig.commentType"
                      label={"Character Limit"}
                      labelProp="name"
                      valueProp="value"
                      defaultValue={1}
                      options={CommentCharacterLimit}
                    />
                    {watch("commentConfig.commentType") === 2 && (
                      <Fragment>
                        <Box mt={2}>
                          <TextInput
                            name="commentConfig.commentMin"
                            onChangeCb={onSubmitHandler}
                            label="Min limit"
                            onBlur={handleBlurTrigger}
                            rules={{ ...requiredRules, ...commentRules }}
                          />
                        </Box>
                        <Box mt={2}>
                          <TextInput
                            name="commentConfig.commentMax"
                            onChangeCb={onSubmitHandler}
                            label="Max limit"
                            onBlur={handleBlurTrigger}
                            rules={{ ...requiredRules, ...commentRules }}
                          />
                        </Box>
                      </Fragment>
                    )}
                  </Fragment>
                )}
              {startRatingDynamicTypes.includes(
                String(surveyInfo.details?.type) as ECampaignSurveyType
              ) ? (
                <Fragment>
                  <Box mt={2}>
                    <TextInput
                      name="metricConfig.customStartLength"
                      label="Range Start"
                      onChangeCb={onSubmitHandler}
                      rules={{ ...requiredRules, ...metricRules }}
                    />
                  </Box>
                  <Box mt={2}>
                    <TextInput
                      name="metricConfig.customEndLength"
                      onChangeCb={onSubmitHandler}
                      label="Range End"
                      rules={{ ...requiredRules, ...metricRules }}
                    />
                  </Box>
                </Fragment>
              ) : null}
            </Fragment>
          )}
        </>
      )}
    </Box>
  );
};

export default QuestionTab;
